import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { HiChevronRight } from 'react-icons/hi';
import api from 'api';
import type { EngagementScoreResponse } from 'api/rest/engagement_score';
import { Modal, ModalHeader, Form, Section } from 'components/common/Modal';
import { FEATURE } from 'constant/features';
import { useHasFeature } from 'hooks/useHasFeature';
import { RootState } from 'redux/store';
import * as S from './EngagementScore.styled';
import { humanizeRuleName, getValueTone } from './utils';

/**
 * "Client Engagement" card rendered below Quick Actions in the client view.
 *
 * Gated by the CLIENT_ENGAGEMENT company entitlement. Clicking the compact
 * score surface opens a modal with the summary, recommended actions, and
 * full score breakdown.
 */
const EngagementScore = () => {
    const { t } = useTranslation();
    const { language_setting: language } = useSelector((state: RootState) => state.user.entity);
    const { entity: activeApp } = useSelector((state: RootState) => state.application);
    const applicationId = activeApp?.id;
    const canViewEngagement = useHasFeature(FEATURE.CLIENT_ENGAGEMENT);

    const [data, setData] = useState<EngagementScoreResponse | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<boolean>(false);
    const [modalOpen, setModalOpen] = useState<boolean>(false);

    useEffect(() => {
        if (!canViewEngagement || !applicationId) {
            setData(null);
            setError(false);
            setLoading(false);
            setModalOpen(false);
            return;
        }
        let cancelled = false;
        const fetchScore = async () => {
            setLoading(true);
            setError(false);
            try {
                const resp = await api.engagementScore.get(applicationId.toString());
                if (cancelled) return;
                if (resp?.data) {
                    setData(resp.data);
                } else {
                    setData(null);
                    setError(true);
                }
            } catch (e) {
                if (cancelled) return;
                console.error('Failed to load engagement score', e);
                setError(true);
                setData(null);
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        fetchScore();
        return () => {
            cancelled = true;
        };
    }, [canViewEngagement, applicationId]);

    // NOTE: every hook must be called above the first conditional return so
    // the call order is stable across renders (rules-of-hooks). The early
    // `!canViewEngagement` bailout comes *after* this useMemo.
    const tone = useMemo(
        () => S.getCategoryTone(data?.engagement_category),
        [data?.engagement_category]
    );

    if (!canViewEngagement) {
        return null;
    }

    const eyebrowLabel = t('clientEngagement', {
        lng: language,
        defaultValue: 'Client Engagement',
    });

    const compactHeader = (
        <S.Header>
            <S.Eyebrow>{eyebrowLabel}</S.Eyebrow>
        </S.Header>
    );

    // -- No application selected yet -----------------------------------------
    if (!applicationId) {
        return (
            <S.CardStatic>
                {compactHeader}
                <S.EmptyHint>
                    {t('engagementSelectCase', {
                        lng: language,
                        defaultValue: 'Select a case to see this client\u2019s engagement score.',
                    })}
                </S.EmptyHint>
            </S.CardStatic>
        );
    }

    // -- Loading skeleton ----------------------------------------------------
    if (loading) {
        return (
            <S.CardStatic aria-busy="true">
                {compactHeader}
                <S.SkeletonRow>
                    <S.SkeletonBlock width="84px" height="32px" radius="8px" />
                    <S.SkeletonBlock width="96px" height="20px" radius="999px" />
                </S.SkeletonRow>
            </S.CardStatic>
        );
    }

    // -- Error state ---------------------------------------------------------
    if (error || !data) {
        return (
            <S.CardStatic>
                {compactHeader}
                <S.ErrorBanner role="alert">
                    {t('engagementError', {
                        lng: language,
                        defaultValue:
                            'We couldn\u2019t load the engagement score right now. Please try again later.',
                    })}
                </S.ErrorBanner>
            </S.CardStatic>
        );
    }

    const { engagement_score, engagement_category, summary, recommended_actions, score_breakdown } =
        data;

    const categoryLabel = engagement_category ? engagement_category.replace(/_/g, ' ') : '';
    const hasBreakdown = Array.isArray(score_breakdown) && score_breakdown.length > 0;
    const hasActions = Array.isArray(recommended_actions) && recommended_actions.length > 0;

    const ariaLabel = engagement_category
        ? `${eyebrowLabel}: ${engagement_score} of 100, ${categoryLabel}. ${t(
              'engagementOpenDetails',
              {
                  lng: language,
                  defaultValue: 'Open details',
              }
          )}`
        : `${eyebrowLabel}: ${engagement_score} of 100`;

    return (
        <>
            <S.Card
                type="button"
                onClick={() => setModalOpen(true)}
                aria-haspopup="dialog"
                aria-expanded={modalOpen}
                aria-label={ariaLabel}
            >
                <S.Header>
                    <S.Eyebrow>{eyebrowLabel}</S.Eyebrow>
                    <S.Chevron aria-hidden="true">
                        <HiChevronRight />
                    </S.Chevron>
                </S.Header>
                <S.MainRow>
                    <S.ScoreBlock accent={tone.accent}>
                        <S.ScoreNumber>{engagement_score}</S.ScoreNumber>
                        <S.ScoreTotal>/ 100</S.ScoreTotal>
                    </S.ScoreBlock>
                    {engagement_category && (
                        <S.CategoryBadge bg={tone.bg} border={tone.border} text={tone.text}>
                            {categoryLabel}
                        </S.CategoryBadge>
                    )}
                </S.MainRow>
            </S.Card>

            <Modal
                open={modalOpen}
                onClose={() => setModalOpen(false)}
                size="md"
                ariaLabel={eyebrowLabel}
            >
                <Form
                    onSubmit={(event) => {
                        event.preventDefault();
                    }}
                >
                    <ModalHeader
                        eyebrow={eyebrowLabel}
                        title={t('engagementScoreTitle', {
                            lng: language,
                            defaultValue: 'Engagement insights',
                        })}
                        subtitle={
                            categoryLabel
                                ? t('engagementCurrentlyCategorized', {
                                      lng: language,
                                      defaultValue: 'Currently categorized as {{category}}.',
                                      category: categoryLabel,
                                  })
                                : undefined
                        }
                    />

                    <Section>
                        <S.ModalScoreRow>
                            <S.ModalScoreNumber accent={tone.accent}>
                                <span>{engagement_score}</span>
                                <span>/ 100</span>
                            </S.ModalScoreNumber>
                            {engagement_category && (
                                <S.CategoryBadge bg={tone.bg} border={tone.border} text={tone.text}>
                                    {categoryLabel}
                                </S.CategoryBadge>
                            )}
                        </S.ModalScoreRow>

                        {summary && <S.SummaryText>{summary}</S.SummaryText>}
                    </Section>

                    {hasActions && (
                        <S.ModalSubsection>
                            <S.SectionTitle>
                                {t('engagementRecommendedActions', {
                                    lng: language,
                                    defaultValue: 'Recommended actions',
                                })}
                            </S.SectionTitle>
                            <S.ActionsList>
                                {recommended_actions.map((action, idx) => (
                                    <S.ActionItem key={`${action}-${idx}`}>{action}</S.ActionItem>
                                ))}
                            </S.ActionsList>
                        </S.ModalSubsection>
                    )}

                    {hasBreakdown && (
                        <S.ModalSubsection>
                            <S.SectionTitle>
                                {t('engagementScoreDetails', {
                                    lng: language,
                                    defaultValue: 'Score details',
                                })}
                            </S.SectionTitle>
                            <S.BreakdownList>
                                {score_breakdown.map((item, idx) => (
                                    <S.BreakdownItem key={`${item.rule}-${idx}`}>
                                        <S.BreakdownRule>
                                            {humanizeRuleName(item.rule)}
                                        </S.BreakdownRule>
                                        <S.BreakdownValue tone={getValueTone(item.value)}>
                                            {item.value}
                                        </S.BreakdownValue>
                                    </S.BreakdownItem>
                                ))}
                            </S.BreakdownList>
                        </S.ModalSubsection>
                    )}
                </Form>
            </Modal>
        </>
    );
};

export default EngagementScore;
